<script setup lang="ts">
import { cva } from "class-variance-authority";
import { useToast } from "@/design/composables/use-toast";
import Icon from "./Icon.vue";

/**
 * The one place the product answers back. Mounted once, by the shell.
 *
 * WRITTEN BY HAND, and not on reka's `ToastProvider`, even though reka is already
 * a dependency: what this needs is a live region, a stack and a timer, and reka's
 * toast brings swipe gestures, a hotkey and a focus escape hatch for about ten
 * kilobytes gzipped — five times the whole budget for this phase. The seam is
 * `use-toast`, not this file: if a later phase wants reka's behaviour, it swaps
 * the inside of this component and nothing that CALLS a toast changes.
 *
 * Top and centred, clear of the nav rail on the left and of Ben's orb on the
 * bottom right, which is draggable and therefore cannot be avoided any other way.
 */
withDefaults(
	defineProps<{
		/**
		 * The dismiss button's accessible name — the only word this component owns,
		 * since the message itself comes from whoever raised the toast. English by
		 * default: `design/` knows no dictionary, and the shell that mounts this
		 * passes the sentence in the reader's language.
		 */
		dismissLabel?: string;
	}>(),
	{ dismissLabel: "Dismiss notification" },
);

const toastClass = cva(
	"pointer-events-auto flex max-w-sm items-start gap-2 rounded-xl border px-3 py-2 text-xs shadow-md",
	{
		variants: {
			tone: {
				success: "border-success/30 bg-success-subtle text-success",
				error: "border-danger/30 bg-danger-subtle text-danger",
				info: "border-border bg-surface-raised text-fg",
			},
		},
		defaultVariants: { tone: "info" },
	},
);

const { toasts, dismiss } = useToast();
</script>

<template>
	<!-- The live region is always mounted, empty or not: a region that appears at
	     the same moment as its text is announced by roughly half of readers. -->
	<div
		class="pointer-events-none fixed inset-x-0 top-2 z-[60] flex flex-col items-center gap-2 px-3"
		aria-live="polite"
	>
		<TransitionGroup
			enter-active-class="transition duration-150 ease-out"
			enter-from-class="-translate-y-1 opacity-0"
			leave-active-class="transition duration-150 ease-in"
			leave-to-class="-translate-y-1 opacity-0"
			move-class="transition duration-150"
		>
			<div
				v-for="item in toasts"
				:key="item.id"
				:class="toastClass({ tone: item.tone })"
				:role="item.tone === 'error' ? 'alert' : 'status'"
			>
				<p class="min-w-0 flex-1 leading-relaxed">{{ item.message }}</p>
				<button
					v-if="item.action"
					type="button"
					class="shrink-0 font-medium underline underline-offset-2"
					@click="item.action.run(); dismiss(item.id)"
				>
					{{ item.action.label }}
				</button>
				<button
					type="button"
					class="shrink-0 opacity-60 transition-opacity hover:opacity-100"
					:aria-label="dismissLabel"
					@click="dismiss(item.id)"
				>
					<Icon name="close" class="size-3.5" />
				</button>
			</div>
		</TransitionGroup>
	</div>
</template>
